import { resolveGuidanceCenter, resolveGuidanceItems } from '@toolproof-core/visualization/runtime/_pure/visual-guidance';
import { collectEntityTargetExtentPoints, collectFrameExtentPoints } from '@toolproof-core/visualization/rendering/_pure/frame-bounds';
import type { VisualGuidance, VisualTarget } from '@toolproof-core/visualization/_contract/guidance';
import type { SpaceDerivationFrame } from '@toolproof-core/visualization/derivation/_contract/derivation';
import { conceptPresentationAt } from '@/_pure/episode';
import { resolveWorkflowEpisode } from '@/workflow-runner/episodeCatalog';
import { conceptToolHandles } from '@/workflow-runner/conceptToolTypes';
import { conceptInvocationSelection, invocationGeometry } from '@/workflow-runner/conceptToolInvocation';
import { buildToolItemKey, parseRoleConstellationKey } from '@/workflow/_pure/keyCodec';
import type { RuntimeState } from '@/workflow-runner/_contract/runtime';
import type { WorkflowConceptTarget } from '@/workflow-runner/_contract/episode';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type { Config } from '@/workflow/_contract/config';
import { getResourceTypeRingRadius } from '@/workflow/_pure/config/derivationConfig';
import type { CameraGraph, CameraStation } from '@toolproof-core/visualization/_contract/camera';
import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';
import { teachingFeaturePaths } from './teachingFeatures';

function selection(state: RuntimeState) {
    if (!state.episode || state.episode.error) return undefined;
    const { scene } = resolveWorkflowEpisode(state.episode.episodeHandle, state.sceneIndex ?? 0);
    if (scene.sceneKind !== 'concept') return undefined;
    const beat = scene.beats[state.episode.beatIndex]!;
    return conceptPresentationAt(beat, state.episode).segment
        ?? (beat.presentation?.some(segment => segment.cameraView)
            ? { id: 'opening', effects: [], cameraView: beat.presentation[0]?.cameraView?.startsWith('outside-')
                ? 'outside-start'
                : ['natural-start', 'boolean-turn'].includes(beat.presentation[0]?.cameraView ?? '')
                    ? beat.presentation[0]!.cameraView : 'overview' } : undefined);
}

function targets(target: WorkflowConceptTarget, frame: SpaceDerivationFrame, state: RuntimeState, data: CosmosDataModel): VisualTarget[] {
    if (target.targetKind === 'item' || target.targetKind === 'constellation') return [target];
    if (target.targetKind === 'flag' || target.targetKind === 'connector') return [];
    if (target.targetKind === 'natural-resource-tour') return Array.from({ length: 51 }, (_, value) => ({
        targetKind: 'item', itemKey: `item:entity:Resource:TYPE-Natural:${value}`,
    }));
    if (target.targetKind === 'resource-type-ring') throw new Error('ResourceType-ring emphasis must target the layout guide, not its items.');
    if (target.targetKind === 'invocation-stage') {
        const { stage } = conceptInvocationSelection(state, conceptToolHandles(data));
        return targets({ targetKind: stage === 'selected' || stage === 'invoked' ? 'selected-tool'
            : stage === 'inputs' ? 'invocation-inputs' : 'invocation-outputs' }, frame, state, data);
    }
    if (state.conceptArrangement?.content !== 'tool-types' && state.conceptArrangement?.content !== 'tool-invocation') {
        throw new Error(`Workflow ${target.targetKind} emphasis requires a Tool arrangement.`);
    }
    const handles = conceptToolHandles(data);
    const handle = state.conceptArrangement.content === 'tool-invocation' ? conceptInvocationSelection(state, handles).handle
        : handles[state.conceptTool && state.conceptTool.revision === state.episode?.revision ? state.conceptTool.index : 0];
    if (!handle) throw new Error('Workflow emphasis lost its selected Tool.');
    if (target.targetKind === 'selected-tool') return [{ targetKind: 'item', itemKey: buildToolItemKey(handle) }];
    if (target.targetKind === 'selected-roles') {
        const roles = frame.constellations.flatMap(c =>
            c.constellationKind === 'entity' && parseRoleConstellationKey(c.constellationKey)?.toolHandle === handle
                ? [{ targetKind: 'constellation' as const, constellationKey: c.constellationKey }] : []);
        if (!roles.length) throw new Error('Workflow selected-roles emphasis has no declared roles.');
        return roles;
    }
    if (state.conceptArrangement.content !== 'tool-invocation') {
        throw new Error(`Workflow ${target.targetKind} emphasis requires an invocation arrangement.`);
    }
    const direction = target.targetKind === 'invocation-inputs' ? 'Input: ' : 'Output: ';
    const result = frame.itemCollections.flatMap(c => c.itemCollectionKind === 'entity' ? c.items : [])
        .filter(item => item.itemKey.startsWith('item:entity:ToolInvocation:')
            && frame.itemParametricState?.entityItemByKey?.[item.itemKey]?.label?.aboveItem?.text?.startsWith(direction))
        .map(item => ({ targetKind: 'item' as const, itemKey: item.itemKey }));
    // The tour deliberately removes values during selection and before output revelation.
    if (!result.length && state.conceptArrangement.content === 'tool-invocation'
        && state.conceptArrangement.invocationStage !== 'tour') throw new Error(`Workflow ${target.targetKind} emphasis has no displayed values.`);
    return result;
}

export function deriveConceptTeaching(frame: SpaceDerivationFrame, state: RuntimeState, data: CosmosDataModel, config: Config): VisualGuidance | undefined {
    const segment = selection(state);
    if (!segment || !segment.effects.length) return undefined;
    const color = config.domainPresentation.entities.tools.item?.visualStyle?.activeHighlightColor ?? '#ffcc33';
    const guideEffects = segment.effects.flatMap(effect =>
        effect.effectKind === 'emphasis' && effect.target.targetKind === 'resource-type-ring' ? [effect] : []);
    if (guideEffects.length) {
        if (segment.effects.length !== 1) throw new Error('ResourceType-ring emphasis must not include sphere emphasis.');
        return { key: `${state.episode!.episodeHandle}:${state.episode!.revision}:${segment.id}`,
            guideEmphasis: { constellationKey: 'constellation:entity:TYPE-ResourceType', color, caption: guideEffects[0]!.caption,
                slideDurationMs: 3000 },
            emphases: [], pointerPlacement: { focus: 'targets', depth: 0.15, lift: 1.5 } };
    }
    const tour = segment.effects.find(effect => effect.effectKind === 'emphasis' && effect.target.targetKind === 'natural-resource-tour');
    if (tour?.effectKind === 'emphasis') {
        if (segment.effects.length !== 1) throw new Error('Natural Resource tour requires one tracking border.');
        return { key: `${state.episode!.episodeHandle}:${state.episode!.revision}:${segment.id}`,
            emphases: [], trackingContour: { targets: targets(tour.target, frame, state, data), color } };
    }
    const pathContours: NonNullable<VisualGuidance['pathContours']>[number][] = [];
    const flagContours: NonNullable<VisualGuidance['flagContours']>[number][] = [];
    const emphases = segment.effects.flatMap(effect => {
        if (effect.effectKind !== 'emphasis') throw new Error('Unsupported Workflow concept teaching effect.');
        if (effect.target.targetKind === 'flag' || effect.target.targetKind === 'connector') {
            if (effect.target.targetKind === 'flag') {
                flagContours.push({ key: `flag:${effect.target.itemKey}`, itemKey: effect.target.itemKey, color });
                return [];
            }
            const feature = effect.target;
            pathContours.push(...teachingFeaturePaths(frame, feature).map((points, index) => ({
                key: `${feature.targetKind}:${feature.itemKey}:${index}`, color, points })));
            return [];
        }
        const resolved = targets(effect.target, frame, state, data);
        for (const target of resolved) resolveGuidanceItems(frame, [target]);
        const marks = effect.marks ?? ['contour'] as const;
        return resolved.length ? [{ targets: resolved, color, marks, pointerAnchor: 'flag-or-item' as const, pointerGapPixels: 4,
            ...(effect.caption && marks.includes('pointer') ? { caption: effect.caption } : {}) }] : [];
    });
    const closeSingleItem = segment.cameraView === 'detail' && state.conceptArrangement?.content !== 'tool-types'
        && new Set(emphases.flatMap(emphasis => resolveGuidanceItems(frame, emphasis.targets))).size === 1;
    const shown = closeSingleItem ? emphases.map(emphasis => ({
        targets: emphasis.targets, color: emphasis.color, marks: ['contour'] as const,
    })) : emphases;
    return { key: `${state.episode!.episodeHandle}:${state.sceneIndex}:${state.episode!.beatIndex}:${state.episode!.revision}:${segment.id}`,
        emphases: shown, pathContours, flagContours,
        pointerPlacement: segment.pointerPlacement ?? { focus: 'targets', depth: 0.2, lift: 1.2 } };
}

export function withConceptTeachingCamera(frame: SpaceDerivationFrame, state: RuntimeState, data: CosmosDataModel, config: Config): SpaceDerivationFrame {
    const segment = selection(state);
    if (!segment) return frame;
    frame = withLowTeachingOverview(frame, state, config);
    if (!segment.cameraView) return frame;
    const guidance = deriveConceptTeaching(frame, state, data, config);
    const camera = frame.spaceRenderOptions?.camera;
    if (!camera?.graph?.views.overview) throw new Error('Workflow teaching camera requires an overview.');
    if (['natural-start', 'natural-one', 'natural-two', 'natural-run', 'boolean-turn'].includes(segment.cameraView)) {
        return withResourceTourCamera(frame, state, segment.cameraView);
    }
    if (segment.cameraView.startsWith('outside-')) {
        const radius = getResourceTypeRingRadius(config);
        const target = resolveGuidanceCenter(frame, [{ targetKind: 'item', itemKey: 'item:entity:TYPE-Natural' }]);
        const destinationTarget = resolveGuidanceCenter(frame, [{ targetKind: 'item', itemKey: 'item:entity:TYPE-Boolean' }]);
        const position = (angle: number): [number, number, number] =>
            [Math.cos(angle) * radius * 1.65, radius * 0.12, Math.sin(angle) * radius * 1.65];
        const start = position(Math.PI - 0.45), end = position(-0.45);
        const view = `lesson:${state.episode!.episodeHandle}:${state.episode!.revision}:${segment.cameraView}`;
        const graph: CameraGraph = {
            stations: { overview: { stationKind: 'explicit', position: start, fov: 65 },
                [view]: { stationKind: 'explicit', position: segment.cameraView === 'outside-end' ? end : start, fov: 65 } },
            views: { overview: { station: 'overview', target: { anchorKind: 'position', position: target } },
                [view]: { station: view, target: { anchorKind: 'position',
                    position: segment.cameraView === 'outside-end' ? destinationTarget : target } } },
            paths: [{ pathId: `overview->${view}`, from: 'overview', to: view, pathKind: 'straight' }],
        };
        return { ...frame, spaceRenderOptions: { ...frame.spaceRenderOptions, camera: { ...camera,
            pose: { position: start, target, fov: 65 }, graph, activeView: view, navigationMode: 'guided',
            navigationDurationMs: segment.cameraView === 'outside-end' ? 7000 : 0,
            navigationOrbitCenter: [0, 0, 0],
        } } };
    }
    const view = `lesson:${state.episode!.episodeHandle}:${state.sceneIndex}:${state.episode!.beatIndex}:${state.episode!.revision}:${segment.cameraView === 'overview' ? 'overview' : segment.id}`;
    const detailTargets: VisualTarget[] = state.conceptArrangement?.content === 'tool-types'
        ? [{ targetKind: 'constellation', constellationKey: 'constellation:entity:TYPE-Tool' },
            { targetKind: 'item', itemKey: 'item:entity:TYPE-Natural' },
            { targetKind: 'item', itemKey: 'item:entity:TYPE-Boolean' }]
        : guidance?.emphases?.flatMap(e => e.targets) ?? [];
    const featurePoints = [...(guidance?.pathContours?.flatMap(path => [...path.points]) ?? []),
        ...segment.effects.flatMap(effect => effect.effectKind === 'emphasis' && effect.target.targetKind === 'flag'
            ? teachingFeaturePaths(frame, effect.target,
                state.conceptArrangement?.content === 'resource-type-universe' ? [0, 0.28, 1] : undefined).flat() : [])];
    if (segment.cameraView === 'detail' && !detailTargets.length && !featurePoints.length) throw new Error('Workflow detail camera has no targets.');
    const points = segment.cameraView === 'detail' ? [
        ...(detailTargets.length ? collectEntityTargetExtentPoints(frame, new Set(resolveGuidanceItems(frame, detailTargets))) : []),
        ...featurePoints,
    ] : [];
    const center = segment.cameraView === 'detail' ? detailTargets.length
        ? resolveGuidanceCenter(frame, detailTargets)
        : featurePoints.reduce<Vector3Tuple>((sum, point) => [sum[0] + point[0] / featurePoints.length,
            sum[1] + point[1] / featurePoints.length, sum[2] + point[2] / featurePoints.length], [0, 0, 0]) : undefined;
    if (center) for (const x of [-4, 4]) for (const y of [-4, 4]) for (const z of [-4, 4]) {
        points.push([center[0] + x, center[1] + y, center[2] + z]);
    }
    const overview = camera.graph.views.overview;
    return { ...frame, spaceRenderOptions: { ...frame.spaceRenderOptions, camera: { ...camera,
        navigationMode: 'guided', activeView: view, navigationDurationMs: 1100,
        navigationOrbitCenter: state.conceptArrangement?.content === 'resource-type-universe' ? null : [0, 0, 0],
        graph: { ...camera.graph,
            stations: { ...camera.graph.stations, [view]: center
                ? { stationKind: 'framing', framing: { framingKind: 'extent',
                    bounds: { center, points }, direction: state.conceptArrangement?.content === 'resource-type-universe'
                        ? [0, 0.28, 1] : lowDetailDirection(center), padding: 1.8 }, fov: 55 }
                : camera.graph.stations[overview.station]! },
            views: { ...camera.graph.views, [view]: center
                ? { station: view, target: { anchorKind: 'position', position: center } }
                : { ...overview, station: view } },
            paths: [
                ...camera.graph.paths,
                ...Object.keys(camera.graph.views).filter(from => from !== view).map(from => ({
                    pathId: `${from}->${view}`, from, to: view, pathKind: 'straight' as const,
                })),
            ],
        },
    } } };
}

function withResourceTourCamera(frame: SpaceDerivationFrame, state: RuntimeState, cameraView: string): SpaceDerivationFrame {
    const camera = frame.spaceRenderOptions!.camera!;
    const natural = (value: number): VisualTarget[] => [{ targetKind: 'item', itemKey: `item:entity:Resource:TYPE-Natural:${value}` }];
    const boolean: VisualTarget[] = ['true', 'false'].map(value => ({
        targetKind: 'item', itemKey: `item:entity:Resource:TYPE-Boolean:${value}`,
    }));
    const selected = cameraView === 'boolean-turn' ? boolean
        : natural(cameraView === 'natural-run' ? 50 : cameraView === 'natural-one' ? 1 : cameraView === 'natural-two' ? 2 : 0);
    const center = resolveGuidanceCenter(frame, selected);
    const start = resolveGuidanceCenter(frame, natural(0));
    const station = (target: Vector3Tuple, items: VisualTarget[]): CameraStation => {
        const radial = Math.hypot(target[0], target[2]);
        const x = target[0] / radial, z = target[2] / radial;
        const points = collectEntityTargetExtentPoints(frame, new Set(resolveGuidanceItems(frame, items)));
        for (const dx of [-4, 4]) for (const dy of [-4, 4]) for (const dz of [-4, 4]) {
            points.push([target[0] + dx, target[1] + dy, target[2] + dz]);
        }
        return { stationKind: 'framing', framing: { framingKind: 'extent', bounds: { center: target, points },
            direction: [-x - z * 0.7, 0.25, -z + x * 0.7], padding: 1.8 }, fov: 55 };
    };
    const view = `lesson:${state.episode!.episodeHandle}:${state.episode!.revision}:${cameraView}`;
    return { ...frame, spaceRenderOptions: { ...frame.spaceRenderOptions, camera: { ...camera,
        navigationMode: 'guided', activeView: view,
        navigationDurationMs: cameraView === 'natural-run' ? 14000 : cameraView === 'boolean-turn' ? 8500
            : cameraView === 'natural-one' || cameraView === 'natural-two' ? 450 : 1600,
        navigationOrbitCenter: cameraView === 'boolean-turn' ? [0, 0, 0] : null,
        graph: { stations: { overview: station(start, natural(0)), [view]: station(center, selected) },
            views: { overview: { station: 'overview', target: { anchorKind: 'position', position: start } },
                [view]: { station: view, target: { anchorKind: 'position', position: center } } },
            paths: [{ pathId: `overview->${view}`, from: 'overview', to: view, pathKind: 'straight' }] },
    } } };
}

function lowDetailDirection(center: Vector3Tuple): Vector3Tuple {
    const distance = Math.hypot(center[0], center[2]);
    return distance > 1 ? [-center[0] / distance, 0.28, -center[2] / distance] : [0, 0.28, 1];
}

function withLowTeachingOverview(frame: SpaceDerivationFrame, state: RuntimeState, config: Config): SpaceDerivationFrame {
    const camera = frame.spaceRenderOptions?.camera;
    if (!camera?.graph?.views.overview) throw new Error('Workflow teaching camera requires an overview.');
    const content = state.conceptArrangement?.content;
    // Long Resource radials deliberately use the existing low interior view, not a fit of every panel.
    const interior = content === 'resource-types-and-resources' || content === 'mock-resource-types-and-resources'
        || content === 'product-resources' || content === 'sum-resources';
    if (content === 'tool-types' || content === 'tool-invocation') {
        const radius = content === 'tool-invocation' ? invocationGeometry(config).ringRadius : getResourceTypeRingRadius(config);
        return { ...frame, spaceRenderOptions: { ...frame.spaceRenderOptions, camera: { ...camera,
            graph: { ...camera.graph,
                stations: { ...camera.graph.stations, overview: { stationKind: 'explicit', position: [0, radius * 0.5, radius * 2], fov: 65 } },
                views: { ...camera.graph.views, overview: { station: 'overview', target: { anchorKind: 'position', position: [0, 0, 0] } } },
            } } } };
    }
    if (!interior) {
        const points = collectFrameExtentPoints(frame);
        if (!points.length) throw new Error('Workflow teaching overview has no geometry.');
        const center: Vector3Tuple = [0, 0, 0];
        for (const axis of [0, 1, 2] as const) {
            center[axis] = (Math.min(...points.map(p => p[axis])) + Math.max(...points.map(p => p[axis]))) / 2;
        }
        const station: CameraStation = { stationKind: 'framing', framing: { framingKind: 'extent',
            bounds: { center, points }, direction: [0, 0.28, 1], padding: 1.5 }, fov: 60 };
        return { ...frame, spaceRenderOptions: { ...frame.spaceRenderOptions, camera: { ...camera,
            graph: { ...camera.graph, stations: { ...camera.graph.stations, overview: station },
                views: { ...camera.graph.views, overview: { station: 'overview', target: { anchorKind: 'position', position: center } } } } } } };
    }
    return frame;
}
